/**
 * Matching for the jurisdiction type-ahead. Pure functions, so the rules are tested without the page.
 *
 * A choice is stored as "Country – Region" (the separator the backend reads), so "India" as a country
 * is stored as "India – India". Typing "indian" must reach India: a typed word that starts with a
 * name of four letters or more counts as a match, but ranks below a plain prefix match.
 */
import type { JurisdictionChoice } from '../../../api/enterpriseApi';

export const SEPARATOR = ' – ';
export const MIN_QUERY = 2;

/** The value a choice is saved as. */
export function choiceValue(c: JurisdictionChoice): string {
  return `${c.country}${SEPARATOR}${c.name}`;
}

/** Lower-case, accent-free, trimmed, so "Curaçao" and "curacao" match alike. */
const fold = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const words = (s: string): string[] => s.split(/[\s\-–—,()/]+/).filter(Boolean);

/**
 * How well one name answers the typed text. 0 = the name starts with it, 1 = a word inside the name
 * starts with it, 0.25 = the typed text starts with the name ("indian" for "India"), null = no match.
 */
function nameScore(name: string, q: string): number | null {
  const t = fold(name);
  if (!t) return null;
  if (t.startsWith(q)) return 0;
  if (words(t).some((w) => w.startsWith(q))) return 1;
  if (q.length >= 4 && t.length >= 4 && q.startsWith(t)) return 0.25;
  return null;
}

/**
 * A match on the country itself counts for more than a match on a region name, so "india" lists India
 * before Indiana (a US state). Region matches carry a half-point penalty.
 */
function score(c: JurisdictionChoice, q: string): number | null {
  const country = nameScore(c.country, q);
  const region = nameScore(c.name, q);
  const scores = [country, region === null ? null : region + 0.5].filter((s): s is number => s !== null);
  return scores.length ? Math.min(...scores) : null;
}

/**
 * The suggestions for the typed text: best match first, then by country and region name.
 * Choices already taken are left out, so a suggestion can always be added. Nothing is suggested
 * until MIN_QUERY characters are typed.
 */
export function matchJurisdictions(
  choices: JurisdictionChoice[],
  query: string,
  taken: string[] = [],
  limit = 8,
): JurisdictionChoice[] {
  const q = fold(query);
  if (q.length < MIN_QUERY) return [];
  const takenSet = new Set(taken);
  const scored: { c: JurisdictionChoice; s: number }[] = [];
  for (const c of choices) {
    if (takenSet.has(choiceValue(c))) continue;
    const s = score(c, q);
    if (s !== null) scored.push({ c, s });
  }
  scored.sort(
    (a, b) => a.s - b.s || a.c.country.localeCompare(b.c.country) || a.c.name.localeCompare(b.c.name),
  );
  return scored.slice(0, limit).map((x) => x.c);
}
